import React from 'react';
import ActivityAssociations from '../Activity/ActivityAssociations';
import {
  GeneralSelect,
  OptionType,
  SingleValue,
} from 'v2/form/CustomSelectors/GeneralSelect';
import styles from './createActivityModal.module.scss';
import pluralize from 'pluralize-esm';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Activity, ActivityAssociation } from 'v2/models';
import moment, { Moment } from 'moment';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import FormLabel from 'v2/form/FormLabel/FormLabel';
import { Form, FormControl } from 'react-bootstrap';

export interface CreateActivityFields {
  relationships: ActivityAssociation[];
  description: string;
  timestamp?: Moment;
  type: SingleValue<OptionType>;
  id?: number;
}

interface CreateActivityFormProps {
  activityToEdit?: Activity;
  defaultAssociations: ActivityAssociation[];
}

const CreateActivityForm = ({
  activityToEdit,
  defaultAssociations,
}: CreateActivityFormProps) => {
  const { register, control } = useFormContext<CreateActivityFields>();
  const { type, description } = useWatch<CreateActivityFields>();
  const initialDateTime = moment(activityToEdit?.timestamp || moment.now());

  const options: OptionType[] = [
    { label: 'Meeting', value: 'meeting' },
    { label: 'Email', value: 'email' },
    { label: 'Data Evaluation', value: 'data_evaluation' },
    { label: 'Note', value: 'note' },
  ];

  switch (activityToEdit?.type) {
    case 'responded_to_request':
      options.push({
        label: 'Response to Request',
        value: 'responded_to_request',
      });
      break;
    case 'file_created':
      options.push({
        label: 'Created file',
        value: 'file_created',
      });
      break;
    case 'file_deleted':
      options.push({
        label: 'Deleted file',
        value: 'file_deleted',
      });
      break;
    default:
      break;
  }

  return (
    <>
      {!activityToEdit?.system_generated && (
        <div>
          <FormLabel
            label={`Date time`}
            required={false}
            className={styles.modalSubtitle}
          />
          <Controller
            name="timestamp"
            control={control}
            defaultValue={initialDateTime}
            rules={{
              required: true,
              validate: (newTimestamp) => newTimestamp?.isValid(),
            }}
            render={({ field: { onChange, value } }) => {
              return (
                <DateTimePicker
                  className="mb-4 d-flex"
                  value={value}
                  onChange={onChange}
                  timezone="system"
                />
              );
            }}
          />
        </div>
      )}
      <div className="mb-4">
        <FormLabel
          label={`Activity Type`}
          required={false}
          className={styles.modalSubtitle}
        />
        <Controller
          name="type"
          control={control}
          defaultValue={
            activityToEdit
              ? (options.find(
                  (o) => o.value === activityToEdit.type
                ) as SingleValue<OptionType>)
              : { label: '', value: '' }
          }
          rules={{
            required: true,
            validate: (newType) => newType?.label !== '',
          }}
          render={({ field: { onChange, value } }) => {
            return (
              <GeneralSelect
                selectedOption={value}
                options={options}
                handleChange={onChange}
                placeHolder="Select"
                disabled={false}
                data-cy="activity:create-activity-select-type-field"
                customStyles={{
                  control: {
                    width: '100%',
                    borderColor: '#ced4da',
                  },
                  dropdownIndicator: {
                    right: '10px',
                    justifyContent: 'flex-end',
                  },
                }}
              />
            );
          }}
        />
      </div>

      <div>
        <FormLabel
          label={`Activity Description`}
          required={false}
          className={styles.modalSubtitle}
        />
        <Form.Control
          as="textarea"
          rows={8}
          autoComplete="off"
          defaultValue={description}
          data-cy="activity:create-activity-description-field"
          {...register('description', {
            required: true,
            validate: (value) => value.trim() !== '',
          })}
        />
      </div>

      <div>
        <Controller
          name="relationships"
          control={control}
          rules={{
            required: true,
          }}
          render={({ field: { onChange } }) => {
            return (
              <ActivityAssociations
                initialActivityAssociations={defaultAssociations}
                onChange={(associations) => onChange(associations)}
                activity={activityToEdit}
                activityType={
                  type?.label ? pluralize(type.label) : 'The activity'
                }
              />
            );
          }}
        />
      </div>
      {activityToEdit && (
        <FormControl
          type="hidden"
          aria-label="data-vendor-hidden-input"
          {...register('id', { required: true })}
          value={activityToEdit.id}
        />
      )}
    </>
  );
};

export default CreateActivityForm;
